<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  badge: { type: [Number, String], default: 0 },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});

const badgeLabel = computed(() => {
  if (!props.badge || Number(props.badge) <= 0) return '';
  return Number(props.badge) > 99 ? '99+' : props.badge;
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="child-item relative py-0.5 text-n-slate-11 before:bg-n-slate-4 after:border-n-slate-4 after:bg-transparent ltr:ml-3 ltr:pl-3 rtl:mr-3 rtl:pr-3 before:left-0 rtl:before:right-0"
  >
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="group flex h-9 max-w-[13rem] items-center gap-2 rounded-xl px-3 py-1 transition-colors hover:bg-n-alpha-1"
      :class="{
        'active bg-blue-50 text-n-brand font-medium dark:bg-n-alpha-2': active,
      }"
    >
      <component
        :is="component"
        v-if="shouldRenderComponent"
        :label
        :icon
        :active
      />
      <template v-else>
        <Icon v-if="icon" :icon="icon" class="size-4 inline-block" />
        <div class="flex-1 truncate min-w-0">{{ label }}</div>
        <span
          v-if="badgeLabel"
          class="grid h-5 min-w-5 place-content-center rounded-full bg-n-brand px-1.5 text-[11px] font-semibold leading-none text-white"
        >
          {{ badgeLabel }}
        </span>
      </template>
    </component>
  </Policy>
</template>
